/*
  A game page's header (Robert, 2026-10-20: make the inner pages "a bit more modern … if
  there's any other information that should be added … let's try it").

  It replaces a lone 400px logo over a rule. Now it is one card: the game's logo on a white
  tile on the left (game logos are drawn for white, which is why the tile stays white in the
  dark theme), and the game's name and figures on the right:

    - cards graded, the big gold number;
    - its rank by cards graded among the games listed beside it on home (gameRank);
    - its share of every card PCG has graded (/counts totalGraded, the home card's figure);
    - its set count, and the language toggle (LanguageToggle) where the passive "Graded in"
      chips were (docs/specs/pop-language-split.md D9);
    - beside the toggle, the compact "Graded in" chart (docs/specs/pop-report-followups.md
      R15b, D14), so the language mix is the first thing seen. It covers the whole game,
      not the toggle's bucket; the bucket only picks which bar is highlighted (D9).

  Cards graded and Sets graded are the chosen language bucket's: the sum and count over its
  live sets (D7). They only appear once the whole catalogue is in, and until then each
  holds its tile with a muted "…" rather than a partial number (D8). Rank and the share of
  all PCG grades stay whole-game, so the share keeps reading the game's own count.

  A figure that cannot be worked out (no count, an archived game, /counts not back yet) is
  left out rather than shown as 0. The h1 is the game's name, visible now; the logo is
  decoration beside it, so its alt is empty.
*/

import {
  HEADER_CARD,
  HEADER_EYEBROW,
  HEADER_GLOW,
  HEADER_TITLE,
  HeaderFigure as Figure,
  HeaderRule,
} from 'components/PageHeaderCard';
import GradedLanguagesChart, { ChartHighlight } from 'components/GradedLanguagesChart';
import LanguageToggle from 'components/LanguageToggle';
import { HERO_IMG, TILE_CHIP } from 'components/styles';
import { formatCount, formatShare, gameRank, gradeShare, ordinal } from 'lib';
import type { LangBucket } from 'lib/setLanguage';

import { useGetCountsQuery } from 'services/pcgPublic';
import type { GameSummaryDto } from 'types/v2/catalog';

/* One bucket's live sets, combined by name, and the cards graded across them. */
export interface BucketFigures {
  cardsGraded: number;
  sets: number;
}

interface GameHeaderProps {
  game: GameSummaryDto;
  games: GameSummaryDto[];
  /*
    The bucket's figures; 'pending' while the catalogue walk is unfinished (D8); null when
    there is nothing honest to show — the walk failed — and the two tiles are left out.
  */
  figures: BucketFigures | 'pending' | null;
  buckets: readonly LangBucket[];
  bucket: LangBucket;
  onBucketChange: (bucket: LangBucket) => void;
}

/* The "Graded in" bar each bucket highlights (D9); Other is many languages, so none. */
const CHART_HIGHLIGHT: Record<LangBucket, ChartHighlight> = {
  en: 'English',
  ja: 'Japanese',
  zh: 'Chinese',
  other: null,
};

/* Holds the figure's place without claiming a number (D8). */
const PENDING = (
  <>
    <span aria-hidden="true" className="text-pcg-textMuted">…</span>
    <span className="sr-only">Loading</span>
  </>
);

const GameHeader = ({ game, games, figures, buckets, bucket, onBucketChange }: GameHeaderProps) => {
  const counts = useGetCountsQuery();
  const rank = gameRank(games, game.gameCode);
  const graded = typeof game.cardsGraded === 'number' ? game.cardsGraded : null;
  const total = counts.data?.totalGraded;
  const share = graded !== null && typeof total === 'number' && total > 0 ? gradeShare(graded, total) : null;

  return (
    <section
      aria-labelledby="game-heading"
      className={`${HEADER_CARD} grid gap-6 md:grid-cols-[minmax(0,340px)_minmax(0,1fr)] md:items-center lg:gap-8`}
      style={HEADER_GLOW}
    >
      <div className="flex h-[120px] items-center justify-center rounded-xl bg-[#ffffff] p-5 sm:h-[180px] sm:p-6 md:h-[200px]">
        {game.imageUrl ? (
          <img src={game.imageUrl} alt="" {...HERO_IMG} className="max-h-full max-w-full object-contain" />
        ) : null}
      </div>

      <div className="min-w-0">
        <p className={HEADER_EYEBROW}>
          Population Report{rank ? ` · ${rank.category}` : ''}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 id="game-heading" className={HEADER_TITLE}>
            {game.name}
          </h1>
          {/* An archived game is listed only for its gradings; say so. */}
          {game.archived ? <span className={TILE_CHIP}>Archived</span> : null}
        </div>
        <HeaderRule />

        <dl className="mt-5 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          {figures !== null ? (
            <Figure
              label="Cards graded"
              value={figures === 'pending' ? PENDING : formatCount(figures.cardsGraded)}
              accent
            />
          ) : graded !== null ? (
            /* The catalogue failed to load, so no bucket figure exists: the whole game's. */
            <Figure label="Cards graded" value={formatCount(graded)} accent />
          ) : null}
          {rank ? (
            <Figure label={`Rank among ${formatCount(rank.of)} ${rank.category === 'Sports' ? 'sports games' : 'TCGs'}`} value={ordinal(rank.rank)} />
          ) : null}
          {share !== null ? <Figure label="Of all PCG grades" value={formatShare(share)} /> : null}
          {figures !== null ? (
            <Figure
              label="Sets graded"
              value={figures === 'pending' ? PENDING : formatCount(figures.sets)}
            />
          ) : null}
        </dl>

        {/*
          Toggle and chart side by side where the column is wide enough: a phone stacks the
          chart under the toggle at full width (R15b), and so does md, where the logo
          column leaves too little room beside a fixed-width panel. Both carry their own
          mt-4, so a hidden toggle (one bucket) or chart (loading) leaves no gap, and the
          chart keeps to the right with ml-auto when the toggle is not drawn.
        */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between sm:gap-6 md:flex-col md:gap-0 lg:flex-row lg:gap-6">
          <LanguageToggle buckets={buckets} value={bucket} onChange={onBucketChange} />
          {/* Keyed by game: the header stays mounted from game to game, and an open
              All languages list should not carry over to the next one. */}
          <GradedLanguagesChart
            key={game.id}
            gameId={game.id}
            highlight={CHART_HIGHLIGHT[bucket]}
            variant="compact"
            className="mt-4 w-full shrink-0 sm:ml-auto sm:w-64 md:w-full lg:w-72"
          />
        </div>
      </div>
    </section>
  );
};

export default GameHeader;
